"use client"

import type { JobAdDetail } from "@/modules/jobAdDetail/types"
import type { FunctionComponent, PropsWithChildren } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { ExternalLink } from "@jobteaser/spark/components/Icons/ExternalLink"

import { usePageLayoutContext } from "@/modules/jobAdDetail/components/PageLayout/PageLayoutContext"
import { LOCAL_STORAGE_APPLIED_EXTERNAL_JOB_ADS } from "@/modules/jobAdDetail/constants"
import { useJobAdDetailTracking } from "@/modules/jobAdDetail/hooks/useJobAdDetailTracking"
import { useLocale } from "@/modules/locales/useLocale"

import styles from "./ApplyButton.module.css"

type ExternalApplyButtonProps = PropsWithChildren<{
  baseUrl: string
  jobAdId: JobAdDetail["id"]
  withSimilarJobAds?: boolean
}>

export const ExternalApplyButton: FunctionComponent<ExternalApplyButtonProps> = ({
  baseUrl,
  children,
  jobAdId,
  withSimilarJobAds = false,
}) => {
  const currentLocale = useLocale()
  const { trackJobAdDetailApplyClick } = useJobAdDetailTracking()
  const { openApplicationFlow } = usePageLayoutContext()

  const handleClick = (): void => {
    trackJobAdDetailApplyClick("external", "job_ad_page")

    window.open(`${baseUrl}/${currentLocale}/job_offers/${jobAdId}/apply_to_extern_url`, "_blank", "noopener")

    if (!withSimilarJobAds) {
      return
    }

    // Save the "applied" job ad id in local storage to display similar job ads
    const currentValues = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_APPLIED_EXTERNAL_JOB_ADS) || "[]")
    const newValues = [...new Set([...currentValues, jobAdId]).values()]
    window.localStorage.setItem(LOCAL_STORAGE_APPLIED_EXTERNAL_JOB_ADS, JSON.stringify(newValues))
    openApplicationFlow("similarJobAds")
  }

  return (
    <Button
      className={styles.main}
      data-testid="jobad-DetailView__CandidateActions__Buttons_apply_external_candidacy"
      icon={ExternalLink}
      variant="highlight"
      onClick={handleClick}
      // data for campaign marketing purpose
      data-aggregators-tag
    >
      {children}
    </Button>
  )
}
